import {
  EntityState,
  PayloadAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import { RootState } from "utils/redux/store";
import { responseStatus } from "utils/types";
import { request } from "utils/request";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import {
  aiServiceFetch,
  aiServiceStreamingRequest,
  isUsingAIService,
  loginlessStreamingRequest,
  StreamError,
  waitForConfigCatFlags,
} from "utils/aiServiceRequest";
import { decodeStringArray } from "utils/helperFunctions";
import {
  ChatbotCoachingPlan,
  ChatbotConversation,
  ChatbotConversationSummary,
  ChatbotMessages,
  ChatbotResponse,
  CoachbotTemplatePrompt,
  InstructionsPayload,
  RecurringChat,
  ProcessSampleConversationResponse,
  TranslatedIntroMessagesAndPreselectedPrompts,
} from "./types";
import { chatbotConversationAdaptor } from "./adaptors";
import {
  dismissNotification,
  selectAllChatWithDirectReportNotifications,
} from "app/components/Notifications/slice";
import { getChatWithDirectReportIntroMessage } from "./helpers";
import {
  setLoginlessConversationCache,
  setLoginlessMessageLimitReached,
  setUIState,
} from "../LoginlessWeeklyCheckinView/slice";
import { updateCompanySettingsAction } from "../AdminConsole/slice";
import { updateUserInfoById } from "../Global/slice";

// ------------------ State Type/Structure ------------------
export interface ChatbotState {
  isCoachbotOpen: boolean;
  showFeedbackCardOnClose: boolean;
  chatbotMessages: ChatbotMessages | null;
  threadId: string | null;
  hidingChatbotButtonFromRoot: boolean;
  selectedChatbotPrompt: CoachbotTemplatePrompt | null;
  savedChatbotConversations: EntityState<ChatbotConversation>;
  activeCoachingPlan: ChatbotCoachingPlan | null;
  recurringChat: RecurringChat | null;
  chatbotFullScreen: boolean;
  coachBoAvailableLanguages: string[];
  deleteConversationStatusById: {
    [threadId: string]: responseStatus;
  };
  sampleChatState: {
    isSampleChatShowing: boolean;
    isCheckingSampleChat: boolean;
  };
  // Streaming state for AI Service SSE responses
  streamingResponse: string;
  isStreaming: boolean;
  activeToolCall: string | null;
  completedToolCalls: { label: string; success: boolean }[];
  translatedIntroMessagesAndPreselectedPrompts: {};
  getCoachBoTranslatedDataStatus: responseStatus;
  getCoachBoAvailableLanguagesStatus: responseStatus;
  processConversationStatus: responseStatus;
  processSampleConversationStatus: responseStatus;
  processSampleConversationResponse: ProcessSampleConversationResponse | null;
  putCoachingPlanStatus: responseStatus;
  editSummaryStatus: responseStatus;
  getRecurringChatStatus: responseStatus;
  putRecurringChatStatus: responseStatus;
  getCoachingPlanStatus: responseStatus;
  sendMessageStatus: responseStatus;
  directReportAuthError: boolean;
  submittingCoachbotFeedbackStatus: responseStatus;
  generateInstructionsStatus: responseStatus;
  getCoachBoConversationsStatus: responseStatus;
  deleteAllConversationsStatus: responseStatus;
  isThreadIdWeeklyCheckinMap: {
    [threadId: string]: boolean;
  };
  isRecentChatsNavOpen: boolean;
}

// ------------------ InitialState ------------------
const initialState: ChatbotState = {
  isCoachbotOpen: false,
  showFeedbackCardOnClose: false,
  chatbotMessages: null,
  threadId: null,
  hidingChatbotButtonFromRoot: false,
  selectedChatbotPrompt: null,
  activeCoachingPlan: null,
  chatbotFullScreen: false,
  savedChatbotConversations: chatbotConversationAdaptor.getInitialState(),
  recurringChat: null,
  sampleChatState: {
    isSampleChatShowing: false,
    isCheckingSampleChat: false,
  },
  coachBoAvailableLanguages: [],
  deleteConversationStatusById: {},
  isThreadIdWeeklyCheckinMap: {},
  isRecentChatsNavOpen: false,
  // Streaming state initialization
  streamingResponse: "",
  isStreaming: false,
  activeToolCall: null,
  completedToolCalls: [],
  translatedIntroMessagesAndPreselectedPrompts: {},
  getCoachBoAvailableLanguagesStatus: "idle",
  getCoachBoTranslatedDataStatus: "idle",
  processConversationStatus: "idle",
  processSampleConversationStatus: "idle",
  processSampleConversationResponse: null,
  putCoachingPlanStatus: "idle",
  editSummaryStatus: "idle",
  getRecurringChatStatus: "idle",
  putRecurringChatStatus: "idle",
  getCoachingPlanStatus: "idle",
  sendMessageStatus: "idle",
  directReportAuthError: false,
  submittingCoachbotFeedbackStatus: "idle",
  generateInstructionsStatus: "idle",
  getCoachBoConversationsStatus: "idle",
  deleteAllConversationsStatus: "idle",
};

// ------------------ Helper Functions ------------------
// Helper function to create user message object (reduces duplication)
const createUserMessage = (message: string): ChatbotMessages[0] => ({
  role: "user",
  content: message,
});

// ------------------ Asynchronous API calls ------------------
export const sendGeneralMessage = createAsyncThunk(
  "chatbot/sendGeneralMessage",
  async (
    {
      message,
      instructionsPayload,
    }: {
      message: string;
      instructionsPayload?: InstructionsPayload;
    },
    { dispatch, getState }
  ) => {
    const { threadId, chatbotMessages } = (getState() as RootState).chatbot;

    const messages = [...(chatbotMessages || []), createUserMessage(message)];

    dispatch(dismissChattingWithNewDirectReportIfAny());
    dispatch(setChatbotMessages(messages));
    dispatch(setShowFeedbackCardOnClose(true)); // when sending a message we want to show the feedback card on close
    if (!threadId) {
      await dispatch(generalGenerateInstructions(instructionsPayload ?? {}));
    }
    dispatch(sendMessage());
  }
);

export const sendGeneralLoginlessMessage = createAsyncThunk(
  "chatbot/sendGeneralLoginlessMessage",
  async (
    {
      message,
      encryptedThreadId,
    }: {
      message: string;
      encryptedThreadId: string;
    },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const { chatbotMessages } = state.chatbot;

    const messages = [...(chatbotMessages || []), createUserMessage(message)];

    // Add user message to UI immediately (optimistic update)
    dispatch(setChatbotMessages(messages));

    // Route loginless message based on ConfigCat feature flag
    if (isUsingAIService()) {
      dispatch(sendLoginlessMessageStreaming(encryptedThreadId));
    } else {
      dispatch(sendLoginlessMessageLegacy(encryptedThreadId));
    }
  }
);

export const generalGenerateInstructions = createAsyncThunk(
  "chatbot/generalGenerateInstructions",
  async (payload: InstructionsPayload, { getState, dispatch }) => {
    const { selectedChatbotPrompt } = (getState() as RootState).chatbot;

    // Commenting out until the backend is pushed.
    if (selectedChatbotPrompt?.chatType === "weeklyCheckIn") {
      return await dispatch(
        generateWeeklyCheckInInstructions(!!selectedChatbotPrompt?.isSampleChat)
      );
    }

    if (
      selectedChatbotPrompt?.type === "teamScan" &&
      selectedChatbotPrompt.chatTypeId
    ) {
      switch (selectedChatbotPrompt?.chatType) {
        case "team":
        case "department":
        case "company":
          return await dispatch(
            generateTEAMscanInstructions({
              entityType: selectedChatbotPrompt.chatType,
              entityId: selectedChatbotPrompt.chatTypeId,
              report: selectedChatbotPrompt.report,
              comments: selectedChatbotPrompt.comments,
            })
          );
        default:
      }
    }

    // if personality report then we need to call a different endpoint,
    if (selectedChatbotPrompt?.type === "personalityReport") {
      switch (selectedChatbotPrompt?.chatType) {
        case "workplaceInsights":
        case "managerGuide":
        case "collaborationGuide":
          return await dispatch(
            generatePersonalityDiscussionInstructions({
              chatType: selectedChatbotPrompt.chatType,
              report: selectedChatbotPrompt.report,
              subjectUserAccountId: selectedChatbotPrompt.chatTypeId,
            })
          );
        case "teamPersonalityGuide":
          return await dispatch(
            generatePersonalityDiscussionInstructions({
              chatType: selectedChatbotPrompt.chatType,
              report: selectedChatbotPrompt.report,
            })
          );
        default:
      }
    }

    if (selectedChatbotPrompt?.type === "onboardingChat") {
      return await dispatch(
        getOnboardingChat(selectedChatbotPrompt.chatTypeId)
      );
    }

    if (
      selectedChatbotPrompt?.type === "chatWithDirectReport" &&
      selectedChatbotPrompt.chatTypeId
    ) {
      return await dispatch(
        getDirectReportChatThreadId(selectedChatbotPrompt.chatTypeId)
      );
    }

    if (
      selectedChatbotPrompt?.type === "goals" ||
      selectedChatbotPrompt?.type === "orgGoals"
    ) {
      return await dispatch(
        generateGoalsInstructions(selectedChatbotPrompt?.type === "orgGoals")
      );
    }

    const generalInstructionsPayload = { ...payload };
    if (selectedChatbotPrompt?.chatTypeId) {
      generalInstructionsPayload.chatTypeId = selectedChatbotPrompt.chatTypeId;
    }
    if (selectedChatbotPrompt?.chatType) {
      generalInstructionsPayload.chatType = selectedChatbotPrompt.chatType;
    }

    // if no special case then we call the regular generate instructions
    await dispatch(generateInstructions(generalInstructionsPayload));
  }
);

// ------------------------ GET Functions ------------------------
// This endpoint returns back the active coaching plan for the user
export const getCoachingPlan = createAsyncThunk(
  "chatbot/getCoachingPlan",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachingPlan`;
    const response = (await request(requestUrl)) as ChatbotCoachingPlan;
    if (Object.keys(response).length === 0) {
      return null;
    }
    return response;
  },
  {
    condition: (_, { getState }) => {
      const {
        chatbot: { getCoachingPlanStatus },
      } = getState() as RootState;
      if (getCoachingPlanStatus === "loading") {
        return false;
      }
    },
  }
);

// This function is used to send an email to the user to remind them to complete their coaching plan
export const sendCoachingPlanEmail = createAsyncThunk(
  "chatbot/sendCoachingPlanEmail",
  async (emailType: "digest" | "recap") => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/sendCoachingPlanEmail/${emailType}`;
    const response = await request(requestUrl);
    return response;
  }
);

// This endpoint call is used to retreive previous chatbot conversations
// We will then use this to pick up where the user left off if the user selects a previous conversation
export const getCoachBoConversations = createAsyncThunk(
  "chatbot/getCoachBoConversations",
  async () => {
    await waitForConfigCatFlags();
    if (isUsingAIService()) {
      const json = await aiServiceFetch<{ data: ChatbotConversation[] }>(
        "/v1/chat/conversations",
        { method: "GET" }
      );
      // AI-Service returns { success: true, data: [...] } with messages already decrypted
      return json.data;
    }

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachBoConversations`;
    const response = (await request(requestUrl)) as (Omit<
      ChatbotConversation,
      "messages"
    > & {
      conversation: string | ChatbotMessages;
    })[];

    const mappedResponse = response.map((c) => ({
      ...c,
      messages:
        typeof c.conversation === "string"
          ? decodeStringArray(c.conversation)
          : c.conversation,
    }));

    return mappedResponse;
  },
  {
    condition: (
      { refresh }: { refresh: boolean } = {
        refresh: false,
      },
      { getState }
    ) => {
      // if refresh is true then we will always fetch the conversations
      if (refresh) {
        return true;
      }

      const {
        chatbot: { getCoachBoConversationsStatus },
      } = getState() as RootState;

      if (getCoachBoConversationsStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getRecurringChat = createAsyncThunk(
  "chatbot/getRecurringChat",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/recurringChat`;
    const response = (await request(requestUrl)) as RecurringChat | {};
    if (Object.keys(response).length === 0) {
      return null;
    }

    return response as RecurringChat;
  },
  {
    condition: (_, { getState }) => {
      const {
        chatbot: { getRecurringChatStatus },
      } = getState() as RootState;
      if (getRecurringChatStatus === "loading") {
        return false;
      }
    },
  }
);

export const getOnboardingChat = createAsyncThunk(
  "chatbot/getOnboardingChat",
  async (userAccountId?: number) => {
    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: { threadId: string } }>(
        "/v1/chat/generateOnboardingInstructions",
        { body: { userAccountId } }
      );
      return { threadId: data.data.threadId };
    }

    // Fallback to Laravel API
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/onboardingChat`;
    const response = (await request(requestUrl)) as { threadId: string };
    return response;
  },
  {
    condition: (userAccountId) => !!userAccountId,
  }
);

export const getDirectReportChatThreadId = createAsyncThunk(
  "chatbot/getDirectReportChatThreadId",
  async (userAccountId: string | number, { getState, dispatch }) => {
    let response: { threadId: string };

    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: { threadId: string } }>(
        "/v1/chat/getDirectReportChatThreadId",
        { body: { userAccountId } }
      );
      response = { threadId: data.data.threadId };
    } else {
      const requestUrl = `${REACT_APP_API_URL}/talentInsights/getDirectReportChatThreadId/${userAccountId}`;
      response = (await request(requestUrl)) as { threadId: string };
    }

    dispatch(setThreadId(response.threadId));

    const savedConversation = selectChatbotConversationById(
      getState() as RootState,
      response.threadId
    );

    const { usersInfoById, currentUserAccountId } = (getState() as RootState)
      .global;

    // If no saved conversation then we will add the welcome message
    if (!savedConversation) {
      dispatch(
        setChatbotMessages([
          {
            role: "assistant",
            content: getChatWithDirectReportIntroMessage(
              usersInfoById,
              currentUserAccountId,
              userAccountId
            ),
          },
        ])
      );
    }

    if (savedConversation?.messages) {
      dispatch(setChatbotMessages(savedConversation.messages));
    }
  }
);

export const getIsThreadIdWeeklyCheckin = createAsyncThunk(
  "chatbot/getIsThreadIdWeeklyCheckin",
  async (threadId: string, { dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/isThreadIdWeeklyCheckin/${threadId}`;
    const response = (await request(requestUrl)) as {
      isWeeklyCheckIn: boolean;
    };
    dispatch(
      setIsThreadIdWeeklyCheckIn({
        threadId,
        isWeeklyCheckIn: response.isWeeklyCheckIn,
      })
    );
  },
  {
    condition: (threadId, { getState }) => {
      const { isThreadIdWeeklyCheckinMap } = (getState() as RootState).chatbot;
      if (isThreadIdWeeklyCheckinMap[threadId] !== undefined) {
        return false;
      }
    },
  }
);

export const getCoachBoAvailableLanguages = createAsyncThunk(
  "chatbot/getCoachBoAvailableLanguages",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/allCoachBoAvailableLanguages`;
    const response = (await request(requestUrl)) as string[];
    return response;
  },
  {
    condition: (_, { getState }) => {
      const { chatbot } = getState() as RootState;
      return chatbot.getCoachBoAvailableLanguagesStatus === "idle";
    },
  }
);

export const getCoachBoTranslatedIntroMessages = createAsyncThunk(
  "chatbot/getCoachBoTranslatedIntroMessages",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachBoTranslatedIntroMessages`;
    const response = (await request(requestUrl)) as {
      translations: TranslatedIntroMessagesAndPreselectedPrompts;
    };
    return response.translations;
  },
  {
    condition: (
      payload: { forceRefresh: boolean } | undefined,
      { getState }
    ) => {
      const { chatbot } = getState() as RootState;
      return (
        chatbot.getCoachBoTranslatedDataStatus === "idle" ||
        payload?.forceRefresh
      );
    },
  }
);

// ------------------------ POST Functions ------------------------
export const generateInstructions = createAsyncThunk(
  "chatbot/generateInstructions",
  async (payload: InstructionsPayload = {}) => {
    // Check if AI Service should be used
    if (isUsingAIService()) {
      // Transform payload to AI Service format
      const aiPayload = {
        chatType: payload.chatType,
        userContext: {
          language: "English",
          style: "normal",
        },
      };

      const data = await aiServiceFetch<{ data: { threadId: string } }>(
        "/v1/chat/generateInstructions",
        { body: aiPayload }
      );
      return { threadId: data.data.threadId };
    }

    // Fallback to Laravel API
    const generateInstructionsUrl = `${REACT_APP_API_URL}/talentInsights/generateInstructions`;
    const response = (await request(generateInstructionsUrl, {
      method: "POST",
      body: JSON.stringify(payload),
    })) as { threadId: string };
    return response;
  }
);

export const generateTEAMscanInstructions = createAsyncThunk(
  "chatbot/generateTEAMscanInstructions",
  async ({
    entityType,
    entityId,
    report,
    comments = ["no comments"],
  }: {
    entityType: "team" | "department" | "company";
    entityId: number;
    report?: any;
    comments?: any;
  }) => {
    let reportPayload = report;
    let commentsPayload = comments;

    if ((Array.isArray(report) && report.length === 0) || !report) {
      reportPayload = ["no report"];
    }

    if ((Array.isArray(comments) && comments.length === 0) || !comments) {
      commentsPayload = ["no comments"];
    }

    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: { threadId: string } }>(
        "/v1/chat/generateTEAMscanInstructions",
        {
          body: {
            teamData: {
              entityType,
              entityId,
              report: reportPayload,
              comments: commentsPayload,
            },
          },
        }
      );
      return { threadId: data.data.threadId };
    }

    const generateInstructionsUrl = `${REACT_APP_API_URL}/talentInsights/generateTEAMscanInstructions`;
    const response = (await request(generateInstructionsUrl, {
      method: "POST",
      body: JSON.stringify({
        entityType,
        entityId,
        report: reportPayload,
        comments: commentsPayload,
      }),
    })) as { threadId: string };

    return response;
  }
);

export const generatePersonalityDiscussionInstructions = createAsyncThunk(
  "chatbot/generatePersonalityDiscussionInstructions",
  async ({
    report,
    chatType,
    subjectUserAccountId = null,
  }: {
    report: any;
    chatType: string;
    subjectUserAccountId?: number | null;
  }) => {
    let reportPayload = report;

    if ((Array.isArray(report) && report.length === 0) || !report) {
      reportPayload = ["no report"];
    }

    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: { threadId: string } }>(
        "/v1/chat/generatePersonalityInstructions",
        {
          body: {
            personalityData: reportPayload,
            chatType,
            subjectUserAccountId,
          },
        }
      );
      return { threadId: data.data.threadId };
    }

    const generateInstructionsUrl = `${REACT_APP_API_URL}/talentInsights/generatePersonalityInstructions`;
    const response = (await request(generateInstructionsUrl, {
      method: "POST",
      body: JSON.stringify({
        report: reportPayload,
        chatType,
        subjectUserAccountId,
      }),
    })) as { threadId: string };
    return response;
  }
);

export const generateWeeklyCheckInInstructions = createAsyncThunk(
  "chatbot/generateWeeklyCheckInInstructions",
  async (isSample: boolean) => {
    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: { threadId: string } }>(
        "/v1/chat/generateWeeklyCheckInInstructions",
        { body: { isSample } }
      );
      return { threadId: data.data.threadId };
    }

    // Fallback to Laravel API
    const generateInstructionsUrl = `${REACT_APP_API_URL}/talentInsights/generateWeeklyCheckInInstructions`;
    const response = (await request(generateInstructionsUrl, {
      method: "POST",
      body: JSON.stringify({ isSample }),
    })) as { threadId: string };
    return response;
  }
);

export const generateEmployeeCheckInReviewInstructions = createAsyncThunk(
  "chatbot/generateEmployeeCheckInReviewInstructions",
  async ({
    userAccountId,
    weeklyScope = 1,
    forceNew = false,
  }: {
    userAccountId: number;
    weeklyScope?: number;
    forceNew?: boolean;
  }) => {
    const data = await aiServiceFetch<{ data: { threadId: string } }>(
      "/v1/chat/generateEmployeeCheckInReviewInstructions",
      { body: { userAccountId, weeklyScope, forceNew } }
    );
    return { threadId: data.data.threadId };
  }
);

export const generateGoalsInstructions = createAsyncThunk(
  "chatbot/generateGoalsInstructions",
  async (isOrganizationalGoal: boolean) => {
    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: { threadId: string } }>(
        "/v1/chat/generateGoalsInstructions",
        { body: { isOrganizationalGoal } }
      );
      return { threadId: data.data.threadId };
    }

    // Fallback to Laravel API
    const generateInstructionsUrl = `${REACT_APP_API_URL}/talentInsights/generateGoalsInstructions`;
    const response = (await request(generateInstructionsUrl, {
      method: "POST",
      body: JSON.stringify({ isOrganizationalGoal }),
    })) as { threadId: string };
    return response;
  }
);

export const sendMessage = createAsyncThunk(
  "chatbot/sendMessage",
  async (_, { getState, rejectWithValue, dispatch }) => {
    const { threadId, chatbotMessages: messages } = (getState() as RootState)
      .chatbot;

    if (!threadId) {
      return rejectWithValue("threadId is not set");
    }

    // Check if AI Service streaming should be used
    if (isUsingAIService()) {
      // Reset streaming state
      dispatch(setStreamingResponse(""));
      dispatch(setIsStreaming(true));
      dispatch(clearCompletedToolCalls());

      try {
        const result = await aiServiceStreamingRequest(
          threadId,
          messages || [],
          {
            onChunk: (chunk) => {
              dispatch(appendStreamingResponse(chunk));
            },
            onComplete: () => {
              dispatch(setIsStreaming(false));
            },
            onError: () => {
              dispatch(setIsStreaming(false));
              toast.error("Streaming request failed. Please try again.");
            },
            onToolStart: (_name, label) => {
              dispatch(setActiveToolCall(label));
            },
            onToolEnd: (_name, success) => {
              const label = (getState() as RootState).chatbot.activeToolCall;
              if (label) {
                dispatch(addCompletedToolCall({ label, success }));
              }
              dispatch(setActiveToolCall(null));
            },
          }
        );

        return {
          messages: result.messages,
          threadId: result.threadId,
        } as ChatbotResponse;
      } catch (error) {
        dispatch(setIsStreaming(false));
        return rejectWithValue(
          error instanceof Error ? error.message : "Streaming request failed"
        );
      }
    }

    // Fallback to Laravel API
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/chatbot`;

    return (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({
        threadId: threadId,
        messages,
      }),
    })) as ChatbotResponse;
  }
);

/**
 * Shared error handler for loginless message thunks.
 * Dispatches UI state updates and cache updates based on HTTP status codes.
 */
function handleLoginlessError(
  status: number,
  data: any,
  encryptedThreadId: string,
  dispatch: any,
  getState: () => unknown
): string {
  const cachedConversation = (getState() as RootState).loginlessWeeklyCheckin
    .loginlessConversationCache[encryptedThreadId];

  if (status === 401) {
    dispatch(setUIState("tokenExpired"));
    if (cachedConversation) {
      dispatch(
        setLoginlessConversationCache({
          ...cachedConversation,
          encryptedThreadId,
          uiState: "tokenExpired",
        })
      );
    }
    toast.error(
      "Your session has expired. Please use the latest link from your email."
    );
    return "Token has expired";
  }

  if (status === 400) {
    dispatch(setUIState("invalidToken"));
    if (cachedConversation) {
      dispatch(
        setLoginlessConversationCache({
          ...cachedConversation,
          encryptedThreadId,
          uiState: "invalidToken",
        })
      );
    }
    toast.error(
      "Invalid or corrupted link. Please use the correct link from your email."
    );
    return "Invalid token";
  }

  if (status === 404 || status === 500) {
    dispatch(setUIState("serverError"));
    if (cachedConversation) {
      dispatch(
        setLoginlessConversationCache({
          ...cachedConversation,
          encryptedThreadId,
          uiState: "serverError",
        })
      );
    }
    toast.error(
      "We're experiencing technical difficulties. Please try again later."
    );
    return "Server error";
  }

  if (status === 429) {
    dispatch(setLoginlessMessageLimitReached(true));
    if (cachedConversation) {
      dispatch(
        setLoginlessConversationCache({
          ...cachedConversation,
          encryptedThreadId,
          uiState: "messageLimitReached",
        })
      );
    }
    toast.error("Message limit exceeded. Maximum 10 user messages allowed.");
    return "Message limit exceeded";
  }

  return data?.message || "Failed to send message";
}

/**
 * Send a loginless message via AI-Service with SSE streaming.
 * Used when isUsingAIService() returns true.
 */
export const sendLoginlessMessageStreaming = createAsyncThunk(
  "chatbot/sendLoginlessMessageStreaming",
  async (
    encryptedThreadId: string,
    { getState, rejectWithValue, dispatch }
  ) => {
    const { chatbotMessages: messages } = (getState() as RootState).chatbot;

    if (!encryptedThreadId) {
      return rejectWithValue("encryptedThreadId is not set");
    }

    dispatch(setStreamingResponse(""));
    dispatch(setIsStreaming(true));

    try {
      const result = await loginlessStreamingRequest(
        encryptedThreadId,
        messages || [],
        {
          onChunk: (chunk) => {
            dispatch(appendStreamingResponse(chunk));
          },
          onComplete: () => {
            dispatch(setIsStreaming(false));
          },
          onError: () => {
            dispatch(setIsStreaming(false));
          },
        }
      );

      return {
        messages: result.messages,
        threadId: result.threadId,
      } as ChatbotResponse;
    } catch (error) {
      dispatch(setIsStreaming(false));

      const streamError = error as StreamError;

      // Handle HTTP errors from the pre-stream validation
      if (streamError.status) {
        const msg = handleLoginlessError(
          streamError.status,
          streamError.data,
          encryptedThreadId,
          dispatch,
          getState
        );
        return rejectWithValue(msg);
      }

      return rejectWithValue(
        error instanceof Error ? error.message : "Streaming request failed"
      );
    }
  }
);

/**
 * Send a loginless message via Laravel API (JSON, no streaming).
 * Used when isUsingAIService() returns false.
 */
export const sendLoginlessMessageLegacy = createAsyncThunk(
  "chatbot/sendLoginlessMessageLegacy",
  async (
    encryptedThreadId: string,
    { getState, rejectWithValue, dispatch }
  ) => {
    const { chatbotMessages: messages } = (getState() as RootState).chatbot;

    if (!encryptedThreadId) {
      return rejectWithValue("encryptedThreadId is not set");
    }

    // Fallback to Laravel API
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/chatbot/loginless`;

    const response = await fetch(requestUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        token: encryptedThreadId,
        messages,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const msg = handleLoginlessError(
        response.status,
        data,
        encryptedThreadId,
        dispatch,
        getState
      );
      return rejectWithValue(msg);
    }

    return {
      messages: data.messages,
      threadId: data.threadId,
    };
  }
);

// This function will be used to make a coaching plan, will initiate a countdown timer for the user to complete the plan
export const postCoachingPlan = createAsyncThunk(
  "chatbot/postCoachingPlan",
  async () => {
    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: ChatbotCoachingPlan }>(
        "/v1/reports/coachingPlan"
      );
      return data.data;
    }

    // Fallback to Laravel API
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachingPlan`;
    const response = await request(requestUrl, {
      method: "POST",
    });
    return response;
  }
);

// ------------------------ PUT Functions ------------------------
// This function should be called when the user closes/ends the conversation
export const processConversation = createAsyncThunk(
  "chatbot/processConversation",
  async (
    {
      threadId,
      isSampleChat,
      isWeeklyCheckInChat,
    }: {
      threadId: string;
      isSampleChat: boolean;
      isWeeklyCheckInChat: boolean;
    },
    { dispatch }
  ) => {
    let response;

    if (isUsingAIService()) {
      response = await aiServiceFetch(
        `/v1/chat/conversations/${threadId}/process`,
        { method: "PUT" }
      );
    } else {
      const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachBoConversations/${threadId}/processConversation`;
      response = await request(requestUrl, {
        method: "PUT",
      });
    }

    // When done processing the conversation we will want to get the latest conversations and latest coaching plan
    if (!isSampleChat && !isWeeklyCheckInChat) {
      dispatch(getCoachBoConversations());
      await dispatch(getCoachingPlan());
    }
    return response;
  }
);

export const processSampleConversation = createAsyncThunk(
  "chatbot/processSampleConversation",
  async ({ threadId }: { threadId: string }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachBoConversations/${threadId}/processSampleConversation`;
    const response = await request(requestUrl, {
      method: "PUT",
    });
    return response;
  }
);

export const putRecurringChat = createAsyncThunk(
  "chatbot/putRecurringChat",
  async (payload: {
    cadence: "weekly" | "daily";
    dayOfWeek?: string | null;
    emailReminderOn: boolean;
    timeOfDayUTC?: string;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/recurringChat`;
    const response = (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    })) as RecurringChat;
    return response;
  }
);

export const editSummary = createAsyncThunk(
  "chatbot/editSummary",
  async (payload: {
    threadId: string;
    summary: string;
    commitment: string;
    title?: string;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachBoConversations/${payload.threadId}/editSummary`;
    const response = (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({
        summary: payload.summary,
        commitment: payload.commitment,
        title: payload.title,
      }),
    })) as ChatbotConversationSummary;
    return response;
  }
);

export const putCoachingPlan = createAsyncThunk(
  "chatbot/putCoachingPlan",
  async ({ id, currentFocus }: { id: number; currentFocus: string }) => {
    if (isUsingAIService()) {
      await aiServiceFetch(`/v1/reports/coachingPlan/${id}`, {
        method: "PUT",
        body: { currentFocus },
      });
      return { id, currentFocus };
    }

    // Fallback to Laravel API
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachingPlan/${id}`;
    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({
        currentFocus,
      }),
    });
    return { id, currentFocus };
  }
);

export const updateUserCoachBoLanguage = createAsyncThunk(
  "chatbot/updateUserCoachBoLanguage",
  async (coachBoLanguage: string, { dispatch, getState }) => {
    const { currentUserAccountId } = (getState() as RootState).global;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/userCoachBoLanguage`;
    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({
        coachBoLanguage,
      }),
    });

    if (currentUserAccountId) {
      dispatch(
        updateUserInfoById({
          userAccountId: currentUserAccountId,
          updatedUserInfo: { coachBoLanguage },
        })
      );
    }

    dispatch(getCoachBoTranslatedIntroMessages({ forceRefresh: true }));
  }
);

export const updateCompanyCoachBoLanguage = createAsyncThunk(
  "chatbot/updateCompanyCoachBoLanguage",
  async (coachBoLanguage: string, { dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/companyCoachBoLanguage`;
    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({
        coachBoLanguage,
      }),
    });
    dispatch(updateCompanySettingsAction({ coachBoLanguage }));
    dispatch(getCoachBoTranslatedIntroMessages({ forceRefresh: true }));
  }
);

// ------------------------ DELETE Functions ------------------------
export const deleteConversation = createAsyncThunk(
  "chatbot/deleteConversation",
  async (threadId: string) => {
    if (isUsingAIService()) {
      await aiServiceFetch(`/v1/chat/conversations/${threadId}`, {
        method: "DELETE",
      });
      return threadId;
    }

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachBoConversations/${threadId}`;
    await request(requestUrl, {
      method: "DELETE",
    });

    return threadId;
  }
);

export const deleteAllConversations = createAsyncThunk(
  "chatbot/deleteAllConversations",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachBoConversations`;
    await request(requestUrl, {
      method: "DELETE",
    });
  }
);

// ------------------------ Helper Functions ------------------------
export const submitCoachbotFeedback = createAsyncThunk(
  "chatbot/submitCoachbotFeedback",
  async (
    payload: {
      feedbackType: "thumbsUp" | "thumbsDown";
      feedback?: string;
    },
    { getState }
  ) => {
    const { chatbotMessages: messages } = (getState() as RootState).chatbot;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/coachBoFeedback`;
    return (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({ ...payload, conversationHistory: messages }),
    })) as { success: boolean };
  }
);

export const setCoachBotPrompt = createAsyncThunk(
  "chatbot/setCoachBotPrompt",
  async (payload: CoachbotTemplatePrompt | null, { dispatch, getState }) => {
    const { currentUserAccountId, usersInfoById } = (getState() as RootState)
      .global;
    const user = currentUserAccountId
      ? usersInfoById[currentUserAccountId]?.firstName
      : "there";

    let coachBotIntro: string = `Hi ${user}`;
    if (payload?.fullBotIntro) {
      coachBotIntro = payload.fullBotIntro;
    } else if (payload?.coachBotIntro) {
      coachBotIntro += ". " + payload.coachBotIntro;
    } else {
      coachBotIntro += ", I’m Coach Bo. What can I help you with?";
    }

    dispatch(
      setChatbotMessages([
        {
          role: "assistant",
          content: coachBotIntro,
        },
        ...(payload?.additionalMessages || []),
      ])
    );
    dispatch(setCoachBotPromptReducer(payload));
    await dispatch(generalGenerateInstructions({}));
  }
);

export const regenerateLastMessage = createAsyncThunk(
  "chatbot/regenerateLastMessage",
  async (_, { dispatch, getState }) => {
    const { chatbotMessages } = (getState() as RootState).chatbot;
    if (!chatbotMessages) return;
    const lastMessage = chatbotMessages?.[chatbotMessages.length - 1];
    if (lastMessage?.role === "assistant") {
      dispatch(setChatbotMessages(chatbotMessages?.slice(0, -1)));
      dispatch(sendMessage());
    }
  }
);

export const dismissChattingWithNewDirectReportIfAny = createAsyncThunk(
  "chatbot/dismissChattingWithNewDirectReportIfAny",
  async (_, { dispatch, getState }) => {
    const { selectedChatbotPrompt } = (getState() as RootState).chatbot;

    if (selectedChatbotPrompt?.chatType !== "chatWithDirectReport") {
      return;
    }
    const chatWithDirectReportNotifications =
      selectAllChatWithDirectReportNotifications(getState() as RootState)?.[0];

    if (
      !chatWithDirectReportNotifications ||
      chatWithDirectReportNotifications?.subjectUserAccountId !==
        selectedChatbotPrompt.chatTypeId
    ) {
      return;
    }

    dispatch(
      dismissNotification(chatWithDirectReportNotifications.notificationId)
    );
  }
);

// ------------------ Beginning of Slice Definition ------------------
export const chatbotReducer = createSlice({
  name: "chatbot",
  initialState,
  reducers: {
    // reset the coachbot to close and clear all the states
    resetChatbotState: (
      state,
      {
        payload,
      }: PayloadAction<
        | undefined
        | {
            keepPrompt: boolean;
            keepChatOpen: boolean;
          }
      >
    ) => {
      const selectedChatbotPrompt = payload?.keepPrompt
        ? state.selectedChatbotPrompt
        : null;
      const isCoachbotOpen = payload?.keepChatOpen
        ? state.isCoachbotOpen
        : false;

      return {
        ...state,
        isCoachbotOpen,
        selectedChatbotPrompt,
        generateInstructionsStatus: "idle",
        directReportAuthError: false,
        sendMessageStatus: "idle",
        submittingCoachbotFeedbackStatus: "idle",
        showFeedbackCardOnClose: false,
      };
    },
    setIsCoachbotOpen: (state, action: PayloadAction<boolean>) => {
      state.isCoachbotOpen = action.payload;
    },
    setCoachBotPromptReducer: (
      state,
      { payload }: PayloadAction<CoachbotTemplatePrompt | null>
    ) => {
      state.selectedChatbotPrompt = payload;
    },
    setCoachbotSuggestedPrompts: (
      state,
      { payload = [] }: PayloadAction<string[]>
    ) => {
      if (!state.selectedChatbotPrompt) {
        state.selectedChatbotPrompt = {
          userPrompts: payload,
        };
        return;
      }
      state.selectedChatbotPrompt.userPrompts = payload;
    },
    setChatbotMessages: (
      state,
      { payload }: PayloadAction<ChatbotMessages | null>
    ) => {
      state.chatbotMessages = payload;
    },
    setHidingChatbotButtonFromRoot: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.hidingChatbotButtonFromRoot = payload;
    },
    setThreadId: (state, { payload }: PayloadAction<string | null>) => {
      state.threadId = payload;
    },
    setShowFeedbackCardOnClose: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showFeedbackCardOnClose = payload;
    },
    updateCoachbotConversation: (
      state,
      { payload }: PayloadAction<ChatbotConversation>
    ) => {
      chatbotConversationAdaptor.upsertOne(
        state.savedChatbotConversations,
        payload
      );
    },
    removeChatBoConversationsFromState: (state) => {
      chatbotConversationAdaptor.removeAll(state.savedChatbotConversations);
    },
    resetCoachingPlan: (state) => {
      state.activeCoachingPlan = null;
    },
    setChatbotFullScreen: (state, { payload }: PayloadAction<boolean>) => {
      state.chatbotFullScreen = payload;
    },
    setIsThreadIdWeeklyCheckIn: (
      state,
      { payload }: PayloadAction<{ threadId: string; isWeeklyCheckIn: boolean }>
    ) => {
      return {
        ...state,
        isThreadIdWeeklyCheckinMap: {
          ...state.isThreadIdWeeklyCheckinMap,
          [payload.threadId]: payload.isWeeklyCheckIn,
        },
      };
    },
    setSampleChatState: (
      state,
      {
        payload,
      }: PayloadAction<{
        isSampleChatShowing: boolean;
        isCheckingSampleChat?: boolean;
      }>
    ) => {
      state.sampleChatState = {
        isSampleChatShowing: payload.isSampleChatShowing,
        isCheckingSampleChat: payload.isCheckingSampleChat ?? false,
      };
    },
    // Streaming state reducers for AI Service SSE
    setStreamingResponse: (state, { payload }: PayloadAction<string>) => {
      state.streamingResponse = payload;
    },
    appendStreamingResponse: (state, { payload }: PayloadAction<string>) => {
      state.streamingResponse += payload;
    },
    setIsStreaming: (state, { payload }: PayloadAction<boolean>) => {
      state.isStreaming = payload;
      if (!payload) state.activeToolCall = null;
    },
    setActiveToolCall: (state, { payload }: PayloadAction<string | null>) => {
      state.activeToolCall = payload;
    },
    addCompletedToolCall: (
      state,
      { payload }: PayloadAction<{ label: string; success: boolean }>
    ) => {
      state.completedToolCalls.push(payload);
    },
    clearCompletedToolCalls: (state) => {
      state.completedToolCalls = [];
    },
    setIsRecentChatsNavOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.isRecentChatsNavOpen = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendGeneralMessage.pending, (state) => {
        state.sendMessageStatus = "loading";
      })
      .addCase(sendGeneralMessage.rejected, (state) => {
        state.sendMessageStatus = "failed";
      })
      .addCase(
        sendMessage.fulfilled,
        (state, action: PayloadAction<ChatbotResponse>) => {
          const messages = [...action.payload.messages];
          if (state.completedToolCalls.length > 0 && messages.length > 0) {
            const last = messages[messages.length - 1];
            if (last.role === "assistant") {
              messages[messages.length - 1] = {
                ...last,
                toolCalls: [...state.completedToolCalls],
              };
            }
          }
          state.chatbotMessages = messages;
          state.completedToolCalls = [];
          state.sendMessageStatus = "succeeded";
          state.streamingResponse = "";
        }
      )
      .addCase(sendMessage.pending, (state) => {
        state.sendMessageStatus = "loading";
      })
      .addCase(sendMessage.rejected, (state) => {
        state.sendMessageStatus = "failed";
        state.streamingResponse = "";
      })
      .addCase(sendGeneralLoginlessMessage.pending, (state) => {
        state.sendMessageStatus = "loading";
      })
      .addCase(sendGeneralLoginlessMessage.rejected, (state) => {
        state.sendMessageStatus = "failed";
      })
      .addCase(
        sendLoginlessMessageStreaming.fulfilled,
        (state, action: PayloadAction<ChatbotResponse>) => {
          state.chatbotMessages = action.payload.messages;
          state.sendMessageStatus = "succeeded";
          state.streamingResponse = "";
        }
      )
      .addCase(sendLoginlessMessageStreaming.pending, (state) => {
        state.sendMessageStatus = "loading";
      })
      .addCase(sendLoginlessMessageStreaming.rejected, (state) => {
        state.sendMessageStatus = "failed";
        state.streamingResponse = "";
      })
      .addCase(
        sendLoginlessMessageLegacy.fulfilled,
        (state, action: PayloadAction<ChatbotResponse>) => {
          const messages = [...action.payload.messages];
          if (state.completedToolCalls.length > 0 && messages.length > 0) {
            const last = messages[messages.length - 1];
            if (last.role === "assistant") {
              messages[messages.length - 1] = {
                ...last,
                toolCalls: [...state.completedToolCalls],
              };
            }
          }
          state.chatbotMessages = messages;
          state.completedToolCalls = [];
          state.sendMessageStatus = "succeeded";
        }
      )
      .addCase(sendLoginlessMessageLegacy.pending, (state) => {
        state.sendMessageStatus = "loading";
      })
      .addCase(sendLoginlessMessageLegacy.rejected, (state) => {
        state.sendMessageStatus = "failed";
      })
      .addCase(generateTEAMscanInstructions.pending, (state) => {
        state.generateInstructionsStatus = "loading";
      })
      .addCase(generateTEAMscanInstructions.rejected, (state) => {
        state.generateInstructionsStatus = "failed";
      })
      .addCase(
        generateTEAMscanInstructions.fulfilled,
        (state, action: PayloadAction<{ threadId: string }>) => {
          state.threadId = action.payload.threadId;
          state.generateInstructionsStatus = "succeeded";
        }
      )
      .addCase(generateInstructions.pending, (state) => {
        state.generateInstructionsStatus = "loading";
      })
      .addCase(generateInstructions.rejected, (state) => {
        state.generateInstructionsStatus = "failed";
      })
      .addCase(
        generateInstructions.fulfilled,
        (state, action: PayloadAction<{ threadId: string }>) => {
          state.threadId = action.payload.threadId;
          state.generateInstructionsStatus = "succeeded";
        }
      )
      .addCase(generatePersonalityDiscussionInstructions.pending, (state) => {
        state.generateInstructionsStatus = "loading";
      })
      .addCase(generatePersonalityDiscussionInstructions.rejected, (state) => {
        state.generateInstructionsStatus = "failed";
      })
      .addCase(
        generatePersonalityDiscussionInstructions.fulfilled,
        (state, action: PayloadAction<{ threadId: string }>) => {
          state.threadId = action.payload.threadId;
          state.generateInstructionsStatus = "succeeded";
        }
      )
      .addCase(generateWeeklyCheckInInstructions.pending, (state) => {
        state.generateInstructionsStatus = "loading";
      })
      .addCase(generateWeeklyCheckInInstructions.rejected, (state) => {
        state.generateInstructionsStatus = "failed";
      })
      .addCase(
        generateWeeklyCheckInInstructions.fulfilled,
        (state, action: PayloadAction<{ threadId: string }>) => {
          state.threadId = action.payload.threadId;
          state.generateInstructionsStatus = "succeeded";
        }
      )
      .addCase(generateEmployeeCheckInReviewInstructions.pending, (state) => {
        state.generateInstructionsStatus = "loading";
      })
      .addCase(generateEmployeeCheckInReviewInstructions.rejected, (state) => {
        state.generateInstructionsStatus = "failed";
      })
      .addCase(
        generateEmployeeCheckInReviewInstructions.fulfilled,
        (state, action: PayloadAction<{ threadId: string }>) => {
          state.threadId = action.payload.threadId;
          state.generateInstructionsStatus = "succeeded";
        }
      )
      .addCase(getOnboardingChat.pending, (state) => {
        state.generateInstructionsStatus = "loading";
        state.directReportAuthError = false;
      })
      .addCase(getOnboardingChat.rejected, (state, action) => {
        state.generateInstructionsStatus = "failed";
        if (action.error.message === "NOT_AUTHORIZED_FOR_DIRECT_REPORT") {
          state.directReportAuthError = true;
        }
      })
      .addCase(
        getOnboardingChat.fulfilled,
        (state, action: PayloadAction<{ threadId: string }>) => {
          state.threadId = action.payload.threadId;
          state.generateInstructionsStatus = "succeeded";
        }
      )
      .addCase(generateGoalsInstructions.pending, (state) => {
        state.generateInstructionsStatus = "loading";
      })
      .addCase(generateGoalsInstructions.rejected, (state) => {
        state.generateInstructionsStatus = "failed";
      })
      .addCase(
        generateGoalsInstructions.fulfilled,
        (state, action: PayloadAction<{ threadId: string }>) => {
          state.threadId = action.payload.threadId;
          state.generateInstructionsStatus = "succeeded";
        }
      )

      .addCase(getDirectReportChatThreadId.pending, (state) => {
        state.generateInstructionsStatus = "loading";
        state.directReportAuthError = false;
      })
      .addCase(getDirectReportChatThreadId.fulfilled, (state) => {
        state.generateInstructionsStatus = "succeeded";
      })
      .addCase(getDirectReportChatThreadId.rejected, (state, action) => {
        state.generateInstructionsStatus = "failed";
        if (action.error.message === "NOT_AUTHORIZED_FOR_DIRECT_REPORT") {
          state.directReportAuthError = true;
        }
      })
      .addCase(submitCoachbotFeedback.fulfilled, (state) => {
        state.submittingCoachbotFeedbackStatus = "succeeded";
      })
      .addCase(submitCoachbotFeedback.pending, (state) => {
        state.submittingCoachbotFeedbackStatus = "loading";
      })
      .addCase(submitCoachbotFeedback.rejected, (state) => {
        state.submittingCoachbotFeedbackStatus = "failed";
      })
      .addCase(getCoachBoConversations.pending, (state) => {
        state.getCoachBoConversationsStatus = "loading";
      })
      .addCase(getCoachBoConversations.rejected, (state) => {
        state.getCoachBoConversationsStatus = "failed";
      })
      .addCase(
        getCoachBoConversations.fulfilled,
        (state, action: PayloadAction<ChatbotConversation[]>) => {
          state.savedChatbotConversations = chatbotConversationAdaptor.setMany(
            state.savedChatbotConversations,
            action.payload
          );

          // Automatically mark conversations with weeklyCheckInLink as weekly check-ins
          action.payload.forEach((conversation) => {
            if (conversation.weeklyCheckInLink) {
              state.isThreadIdWeeklyCheckinMap[conversation.threadId] = true;
            }
          });

          state.getCoachBoConversationsStatus = "succeeded";
        }
      )
      .addCase(getCoachingPlan.pending, (state) => {
        state.getCoachingPlanStatus = "loading";
      })
      .addCase(getCoachingPlan.rejected, (state) => {
        state.getCoachingPlanStatus = "failed";
      })
      .addCase(
        getCoachingPlan.fulfilled,
        (state, action: PayloadAction<ChatbotCoachingPlan | null>) => {
          state.activeCoachingPlan = action.payload;
          state.getCoachingPlanStatus = "succeeded";
        }
      )
      .addCase(processSampleConversation.pending, (state) => {
        state.processSampleConversationStatus = "loading";
      })
      .addCase(processSampleConversation.rejected, (state) => {
        state.processSampleConversationStatus = "failed";
      })
      .addCase(processSampleConversation.fulfilled, (state, action) => {
        state.processSampleConversationStatus = "succeeded";
        // Ensure the payload is of the expected type before assignment
        if (
          action.payload &&
          "summary" in action.payload &&
          "trendAnalysisResponse" in action.payload &&
          "conversationAnalysisResponse" in action.payload
        ) {
          state.processSampleConversationResponse =
            action.payload as ProcessSampleConversationResponse;
        }
      })
      .addCase(
        deleteConversation.pending,
        (
          state,
          {
            meta: { arg: id },
          }: PayloadAction<undefined, string, { arg: string }>
        ) => {
          state.deleteConversationStatusById = {
            ...state.deleteConversationStatusById,
            [id]: "loading",
          };
        }
      )
      .addCase(
        deleteConversation.rejected,
        (
          state,
          { meta: { arg: id } }: PayloadAction<unknown, string, { arg: string }>
        ) => {
          state.deleteConversationStatusById = {
            ...state.deleteConversationStatusById,
            [id]: "failed",
          };
        }
      )
      .addCase(
        deleteConversation.fulfilled,
        (state, action: PayloadAction<string>) => {
          toast.success(
            "Conversation successfully deleted!",
            successNotificationOptions
          );

          chatbotConversationAdaptor.removeOne(
            state.savedChatbotConversations,
            action.payload
          );
          state.deleteConversationStatusById = {
            ...state.deleteConversationStatusById,
            [action.payload]: "succeeded",
          };
        }
      )

      .addCase(putRecurringChat.pending, (state) => {
        state.putRecurringChatStatus = "loading";
      })
      .addCase(putRecurringChat.rejected, (state) => {
        state.putRecurringChatStatus = "failed";
      })
      .addCase(
        putRecurringChat.fulfilled,
        (state, { payload }: PayloadAction<null | RecurringChat>) => {
          const previousRecurringChat = state.recurringChat;
          state.recurringChat = payload;
          state.putRecurringChatStatus = "succeeded";
          toast.success(
            `Recurring chat successfully ${
              previousRecurringChat === null ? "set up" : "updated"
            }!`,
            successNotificationOptions
          );
        }
      )
      .addCase(getRecurringChat.pending, (state) => {
        state.getRecurringChatStatus = "loading";
      })
      .addCase(getRecurringChat.rejected, (state) => {
        state.getRecurringChatStatus = "failed";
      })
      .addCase(
        getRecurringChat.fulfilled,
        (state, { payload }: PayloadAction<null | RecurringChat>) => {
          state.recurringChat = payload;
          state.getRecurringChatStatus = "succeeded";
        }
      )
      .addCase(editSummary.pending, (state) => {
        state.editSummaryStatus = "loading";
      })
      .addCase(editSummary.rejected, (state) => {
        state.editSummaryStatus = "failed";
      })
      .addCase(
        editSummary.fulfilled,
        (
          state,
          { payload: summary }: PayloadAction<ChatbotConversationSummary>
        ) => {
          chatbotConversationAdaptor.updateOne(
            state.savedChatbotConversations,
            { id: summary.threadId, changes: { summary } }
          );

          toast.success(
            "Conversation summary/commitment successfully updated!",
            successNotificationOptions
          );

          state.editSummaryStatus = "succeeded";
        }
      )
      .addCase(putCoachingPlan.pending, (state) => {
        state.putCoachingPlanStatus = "loading";
      })
      .addCase(putCoachingPlan.rejected, (state) => {
        state.putCoachingPlanStatus = "failed";
      })
      .addCase(
        putCoachingPlan.fulfilled,
        (
          state,
          action: PayloadAction<{ id: number; currentFocus: string }>
        ) => {
          if (state.activeCoachingPlan) {
            state.activeCoachingPlan.currentFocus = action.payload.currentFocus;
          }
          toast.success(
            "Current focus successfully updated!",
            successNotificationOptions
          );
          state.putCoachingPlanStatus = "succeeded";
        }
      )
      .addCase(deleteAllConversations.pending, (state) => {
        state.deleteAllConversationsStatus = "loading";
      })
      .addCase(deleteAllConversations.rejected, (state) => {
        state.deleteAllConversationsStatus = "failed";
      })
      .addCase(deleteAllConversations.fulfilled, (state) => {
        chatbotConversationAdaptor.removeAll(state.savedChatbotConversations);
        state.deleteAllConversationsStatus = "succeeded";
      })
      .addCase(processConversation.pending, (state) => {
        state.processConversationStatus = "loading";
      })
      .addCase(processConversation.rejected, (state) => {
        state.processConversationStatus = "failed";
      })
      .addCase(processConversation.fulfilled, (state) => {
        state.processConversationStatus = "succeeded";
      })
      .addCase(getCoachBoAvailableLanguages.pending, (state) => {
        state.getCoachBoAvailableLanguagesStatus = "loading";
      })
      .addCase(getCoachBoAvailableLanguages.fulfilled, (state, action) => {
        state.coachBoAvailableLanguages = action.payload;
        state.getCoachBoAvailableLanguagesStatus = "succeeded";
      })
      .addCase(getCoachBoAvailableLanguages.rejected, (state) => {
        state.getCoachBoAvailableLanguagesStatus = "failed";
      })
      .addCase(getCoachBoTranslatedIntroMessages.pending, (state) => {
        state.getCoachBoTranslatedDataStatus = "loading";
      })
      .addCase(getCoachBoTranslatedIntroMessages.fulfilled, (state, action) => {
        state.translatedIntroMessagesAndPreselectedPrompts = action.payload;
        state.getCoachBoTranslatedDataStatus = "succeeded";
      })
      .addCase(getCoachBoTranslatedIntroMessages.rejected, (state) => {
        state.getCoachBoTranslatedDataStatus = "failed";
      });
  },
});

// ------------------ Selectors ------------------
export const {
  setIsCoachbotOpen,
  resetChatbotState,
  setCoachBotPromptReducer,
  setChatbotMessages,
  setHidingChatbotButtonFromRoot,
  setCoachbotSuggestedPrompts,
  setThreadId,
  setShowFeedbackCardOnClose,
  updateCoachbotConversation,
  removeChatBoConversationsFromState,
  resetCoachingPlan,
  setChatbotFullScreen,
  setIsThreadIdWeeklyCheckIn,
  setSampleChatState,
  // Streaming actions for AI Service
  setStreamingResponse,
  appendStreamingResponse,
  setIsStreaming,
  setActiveToolCall,
  addCompletedToolCall,
  clearCompletedToolCalls,
  setIsRecentChatsNavOpen,
} = chatbotReducer.actions;

export const selectChatbotMessages = (state: RootState) =>
  state.chatbot.chatbotMessages || [];
export const selectThreadId = (state: RootState) => state.chatbot.threadId;
export const selectSendMessageStatus = (state: RootState) =>
  state.chatbot.sendMessageStatus;
export const selectIsCoachbotOpen = (state: RootState) =>
  state.chatbot.isCoachbotOpen;
export const selectSelectedChatbotPrompt = (state: RootState) =>
  state.chatbot.selectedChatbotPrompt;
export const selectHidingChatbotButtonFromRoot = (state: RootState) =>
  state.chatbot.hidingChatbotButtonFromRoot;
export const selectDirectReportAuthError = (state: RootState) =>
  state.chatbot.directReportAuthError;
export const selectSubmittingCoachbotFeedbackStatus = (state: RootState) =>
  state.chatbot.submittingCoachbotFeedbackStatus;
export const selectGenerateInstructionsStatus = (state: RootState) =>
  state.chatbot.generateInstructionsStatus;
export const selectGetCoachBoConversationsStatus = (state: RootState) =>
  state.chatbot.getCoachBoConversationsStatus;
export const selectShowFeedbackCardOnClose = (state: RootState) =>
  state.chatbot.showFeedbackCardOnClose;
export const selectActiveCoachingPlan = (state: RootState) =>
  state.chatbot.activeCoachingPlan;
export const selectGetCoachingPlanStatus = (state: RootState) =>
  state.chatbot.getCoachingPlanStatus;
export const selectDeleteConversationStatusById =
  (threadId: string | undefined | null) => (state: RootState) =>
    state.chatbot.deleteConversationStatusById[threadId ?? ""] || "idle";
export const selectGetRecurringChatStatus = (state: RootState) =>
  state.chatbot.getRecurringChatStatus;
export const selectPutRecurringChatStatus = (state: RootState) =>
  state.chatbot.putRecurringChatStatus;
export const selectRecurringChat = (state: RootState) =>
  state.chatbot.recurringChat;
export const selectEditSummaryStatus = (state: RootState) =>
  state.chatbot.editSummaryStatus;
export const selectPutCoachingPlanStatus = (state: RootState) =>
  state.chatbot.putCoachingPlanStatus;
export const selectDeleteAllConversationsStatus = (state: RootState) =>
  state.chatbot.deleteAllConversationsStatus;
export const selectProcessConversationStatus = (state: RootState) =>
  state.chatbot.processConversationStatus;
export const selectProcessSampleConversationStatus = (state: RootState) =>
  state.chatbot.processSampleConversationStatus;
export const selectProcessSampleConversationResponse = (state: RootState) =>
  state.chatbot.processSampleConversationResponse;
export const selectChatbotFullScreen = (state: RootState) =>
  state.chatbot.chatbotFullScreen;
export const selectIsThreadIdWeeklyCheckinMap = (state: RootState) =>
  state.chatbot.isThreadIdWeeklyCheckinMap;
export const selectAvailableCoachBoLanguages = (state: RootState) =>
  state.chatbot.coachBoAvailableLanguages;
export const selectCurrentChatbotLanguage = (state: RootState) => {
  const currentUserAccountId = state.global.currentUserAccountId;
  if (currentUserAccountId) {
    const currentUser = state.global.usersInfoById[currentUserAccountId];
    // If language is selected by user we are using this language otherwise we are using company settings
    if (currentUser?.coachBoLanguage) {
      return currentUser.coachBoLanguage;
    }
  }

  const currentCompanySettingsCoachBoLanguage =
    state.adminConsole.companySettings?.coachBoLanguage;

  return currentCompanySettingsCoachBoLanguage || "English";
};
export const selectTranslatedIntroMessagesAndPreselectedPrompts = (
  state: RootState
) => state.chatbot.translatedIntroMessagesAndPreselectedPrompts;

export const {
  selectById: selectChatbotConversationById,
  selectAll: selectAllChatbotConversations,
  selectEntities: selectChatbotConversationEntities,
} = chatbotConversationAdaptor.getSelectors<RootState>(
  (state) => state.chatbot.savedChatbotConversations
);

export const selectSampleChatState = (state: RootState) =>
  state.chatbot.sampleChatState;

// Streaming selectors for AI Service
export const selectStreamingResponse = (state: RootState) =>
  state.chatbot.streamingResponse;
export const selectIsStreaming = (state: RootState) =>
  state.chatbot.isStreaming;
export const selectActiveToolCall = (state: RootState) =>
  state.chatbot.activeToolCall;
export const selectCompletedToolCalls = (state: RootState) =>
  state.chatbot.completedToolCalls;
export const selectIsRecentChatsNavOpen = (state: RootState) =>
  state.chatbot.isRecentChatsNavOpen;

export default chatbotReducer.reducer;
